<template>
  <GcsaMobileSidebar
    :open="visible"
    :locale="normalizedLocale"
    :active-nav-key="activeNavKey || undefined"
    :theme="resolvedTheme"
    :logo-title="logoTitle || undefined"
    :logo-tagline="logoTaglineValue"
    :logo-href="logoHref || undefined"
    :nav-items="navItemsParsed"
    :menu-aria-label="menuAriaLabel || undefined"
    :close-aria-label="closeAriaLabel || undefined"
    @close="onClose"
  >
    <slot />
  </GcsaMobileSidebar>
</template>

<script setup lang="ts">
/**
 * Web Component root for <gcsa-mobile-sidebar>
 *   <gcsa-mobile-sidebar open theme="dark" locale="zh-CN"></gcsa-mobile-sidebar>
 *
 * Dispatches a bubbling `close` CustomEvent when the panel requests close.
 * Keep an internal `visible` flag so open/close stays reliable across attribute vs property writes.
 */
import { computed, getCurrentInstance, onMounted, ref, toRef, watch } from 'vue';
import GcsaMobileSidebar from './GcsaMobileSidebar.vue';
import { useCeHostTheme } from '../composables/useCeHostTheme';
import type {
  GcsaChromeLocale,
  GcsaMobileNavItem
} from '../consts/gcsaChromeContent';

const props = withDefaults(
  defineProps<{
    open?: string | boolean;
    locale?: string;
    activeNavKey?: string;
    theme?: string;
    logoTitle?: string;
    logoTagline?: string;
    logoHref?: string;
    /** JSON string of GcsaMobileNavItem[] */
    navItems?: string;
    menuAriaLabel?: string;
    closeAriaLabel?: string;
  }>(),
  {
    open: false,
    locale: 'zh-CN',
    activeNavKey: '',
    theme: '',
    logoTitle: '',
    logoTagline: undefined,
    logoHref: '',
    navItems: '',
    menuAriaLabel: '',
    closeAriaLabel: ''
  }
);

const LOCALE_SET = new Set(['zh-CN', 'zh-TW', 'en-US']);

const parseOpen = (value: string | boolean | undefined): boolean => {
  if (value === true || value === '') return true;
  if (value === false || value == null) return false;
  const normalized = String(value).toLowerCase().trim();
  if (!normalized) return true; // bare `open` attribute
  return normalized === 'true' || normalized === '1' || normalized === 'open';
};

const visible = ref(parseOpen(props.open));

watch(
  () => props.open,
  (value) => {
    visible.value = parseOpen(value);
  }
);

/** Host custom element — captured on mount (getCurrentInstance is null in click handlers). */
let hostEl: (HTMLElement & { open?: boolean | string }) | null = null;

onMounted(() => {
  const instance = getCurrentInstance();
  const raw = instance?.vnode.el as Node | null | undefined;
  const root = raw && 'getRootNode' in raw ? raw.getRootNode() : null;
  if (root instanceof ShadowRoot && root.host instanceof HTMLElement) {
    hostEl = root.host as HTMLElement & { open?: boolean | string };
  }
  // Re-sync in case attribute arrived before mount hydration
  visible.value = parseOpen(props.open);
});

const { resolvedTheme } = useCeHostTheme(toRef(props, 'theme'));

const normalizedLocale = computed<GcsaChromeLocale>(() => {
  const value = props.locale || 'zh-CN';
  return LOCALE_SET.has(value) ? (value as GcsaChromeLocale) : 'zh-CN';
});

const logoTaglineValue = computed(() => {
  if (props.logoTagline === undefined) return undefined;
  return props.logoTagline;
});

const navItemsParsed = computed<GcsaMobileNavItem[] | undefined>(() => {
  if (!props.navItems?.trim()) return undefined;
  try {
    const parsed = JSON.parse(props.navItems) as GcsaMobileNavItem[];
    return Array.isArray(parsed) ? parsed : undefined;
  } catch {
    return undefined;
  }
});

const onClose = () => {
  visible.value = false;
  hostEl?.dispatchEvent(
    new CustomEvent('close', { bubbles: true, composed: true })
  );
  if (hostEl) {
    hostEl.removeAttribute('open');
    try {
      hostEl.open = false;
    } catch {
      /* ignore */
    }
  }
};
</script>

<style>
@import '../css/logo.css';
@import '../css/mobile-sidebar.css';
</style>
